/**
 * Admin Sidebar
 * 관리자 사이드바 (메뉴, 필터링, 배지)
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AdminUser,
  ServiceType,
  canManageServiceType,
} from '../../models';
import {
  ADMIN_MENU_CONFIG,
  filterMenuByRole,
  MenuItem,
  SERVICE_CONFIG,
} from '../../config/adminMenu';
import { useOrganizations, useContacts, useSales } from '../../use-cases';
import { useOrganizationSubscriptions } from '../../../../hooks/useOrganizationSubscriptions';

interface AdminSidebarProps {
  isOpen: boolean;
  currentUser: AdminUser;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isOpen,
  currentUser,
}) => {
  const location = useLocation();
  const [selectedService, setSelectedService] = useState<ServiceType | 'all'>(
    'all'
  );
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  // 배지 카운트를 위한 훅 (에러 처리 포함)
  const { pendingCount: pendingBusinessCount = 0 } = useOrganizations(
    selectedService === 'all' ? undefined : selectedService
  ) || {};
  const { pendingCount: pendingInquiryCount = 0 } = useContacts(
    selectedService === 'all' ? undefined : selectedService
  ) || {};
  const { statistics: salesStats } = useSales(
    selectedService === 'all' ? undefined : selectedService
  );

  // 신규 구독 시스템 사용
  const { items: subscriptionItems = [] } = useOrganizationSubscriptions();

  // 만료 임박 구독 수 계산 (7일 이내)
  const expiringSubscriptionCount = React.useMemo(() => {
    const now = new Date();
    return subscriptionItems.filter((org) => {
      const endsAt = org.plan_ends_at || org.trial_ends_at;
      if (!endsAt) return false;

      const endDate = new Date(endsAt);
      const diffDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      return diffDays >= 0 && diffDays <= 7;
    }).length;
  }, [subscriptionItems]);

  // 역할 기반 메뉴 필터링
  const filteredMenu = filterMenuByRole(ADMIN_MENU_CONFIG, currentUser);

  // 현재 경로에 해당하는 메뉴 자동 확장
  useEffect(() => {
    const expanded = new Set<string>();
    filteredMenu.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          (child) => child.path === location.pathname
        );
        if (hasActiveChild) {
          expanded.add(item.id);
        }
      }
    });
    setExpandedMenus(expanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // filteredMenu 제거 - 매 렌더링마다 실행 방지

  const toggleMenu = (menuId: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      const wasExpanded = newSet.has(menuId);
      if (wasExpanded) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  const isActive = (path?: string) => {
    return path === location.pathname;
  };

  const getBadgeCount = (badgeKey?: string): number => {
    if (!badgeKey) return 0;

    const badgeCounts: Record<string, number> = {
      pendingBusinessCount: pendingBusinessCount || 0,
      pendingInquiryCount: pendingInquiryCount || 0,
      newInquiryCount: salesStats?.new_inquiry || 0,
      expiringSubscriptionCount: expiringSubscriptionCount || 0,
    };

    return badgeCounts[badgeKey] || 0;
  };

  const renderMenuItem = (item: MenuItem, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.has(item.id);
    const active = isActive(item.path);
    const badgeCount = getBadgeCount(item.badge);

    if (hasChildren) {
      const hasActiveChild = item.children?.some(
        (child) => child.path === location.pathname
      );
      return (
        <div key={item.id}>
          <button
            onClick={(e) => toggleMenu(item.id, e)}
            className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100 ${
              isChild ? 'pl-8' : ''
            } ${hasActiveChild ? 'text-indigo-700 bg-indigo-50/50' : ''}`}
          >
            <div className="flex items-center space-x-3">
              <span>{item.icon}</span>
              <span className="font-medium">{item.label}</span>
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isExpanded && (
            <div className="bg-gray-50 border-l-2 border-gray-200 ml-6">
              {item.children?.map((child) => renderMenuItem(child, true))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path || '#'}
        className={`flex items-center justify-between hover:bg-gray-100 ${
          isChild
            ? 'px-4 py-2 text-sm text-gray-600'
            : 'px-4 py-3'
        } ${active
            ? isChild
              ? 'bg-indigo-50 text-indigo-600 font-semibold border-r-4 border-indigo-600'
              : 'bg-indigo-50 text-indigo-600 border-r-4 border-indigo-600'
            : ''
        }`}
      >
        <div className="flex items-center space-x-3">
          {isChild ? (
            <span className="text-sm">{item.icon}</span>
          ) : (
            <span>{item.icon}</span>
          )}
          <span className={active ? 'font-semibold' : ''}>{item.label}</span>
        </div>
        {badgeCount > 0 && (
          <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
            {badgeCount}
          </span>
        )}
      </Link>
    );
  };

  if (!isOpen) return null;

  return (
    <aside className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 overflow-y-auto">
      {/* Service Filter (Operator만 해당) */}
      {currentUser.role === 'operator' && currentUser.service_type && (
        <div className="p-4 border-b border-gray-200 bg-blue-50">
          <p className="text-xs text-gray-600 mb-2">담당 서비스</p>
          <div className="flex items-center space-x-2">
            <span className="text-2xl">
              {SERVICE_CONFIG[currentUser.service_type].icon}
            </span>
            <span className="font-semibold">
              {SERVICE_CONFIG[currentUser.service_type].label}
            </span>
          </div>
        </div>
      )}

      {/* Service Filter (Super Admin만 전체 선택 가능) */}
      {currentUser.role === 'super_admin' && (
        <div className="p-4 border-b border-gray-200">
          <p className="text-xs text-gray-600 mb-2">서비스 필터</p>
          <select
            value={selectedService}
            onChange={(e) =>
              setSelectedService(e.target.value as ServiceType | 'all')
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">전체 서비스</option>
            {Object.entries(SERVICE_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.icon} {config.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Menu Items */}
      <nav className="py-4">
        {filteredMenu.map((item) => renderMenuItem(item))}
      </nav>
    </aside>
  );
};
